import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';

const ArticleGrid = ({ articles }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const articlesPerPage = 6;
  
  const totalPages = Math.ceil(articles.length / articlesPerPage);
  const startIndex = (currentPage - 1) * articlesPerPage;
  const currentArticles = articles.slice(startIndex, startIndex + articlesPerPage);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getCategoryIcon = (category) => {
    const icons = {
      maintenance: 'Wrench',
      chemicals: 'Beaker',
      equipment: 'Settings',
      seasonal: 'Calendar'
    };
    return icons[category] || 'BookOpen';
  };

  const getCategoryColor = (category) => {
    const colors = {
      maintenance: 'bg-primary/10 text-primary',
      chemicals: 'bg-success/10 text-success',
      equipment: 'bg-warning/10 text-warning',
      seasonal: 'bg-accent/10 text-accent'
    };
    return colors[category] || 'bg-secondary/10 text-secondary';
  };

  if (articles.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="Search" size={32} className="text-primary" />
        </div>
        <h3 className="text-xl font-heading font-semibold text-text-primary mb-2">
          No se encontraron artículos
        </h3>
        <p className="text-text-secondary">
          Intenta con otros términos de búsqueda o selecciona una categoría diferente.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Articles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {currentArticles.map((article) => (
          <article
            key={article.id}
            className="bg-surface rounded-lg border border-border overflow-hidden shadow-medium hover:shadow-large nav-transition group"
          >
            <div className="relative overflow-hidden h-48">
              <Image
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover group-hover:scale-105 nav-transition"
              />
              <div className="absolute top-3 left-3">
                <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(article.category)}`}>
                  <Icon name={getCategoryIcon(article.category)} size={12} />
                  <span className="capitalize">{article.category}</span>
                </div>
              </div>
              {article.featured && (
                <div className="absolute top-3 right-3">
                  <div className="bg-accent text-white px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1">
                    <Icon name="Star" size={12} />
                    <span>Destacado</span>
                  </div>
                </div>
              )}
            </div>

            <div className="p-6">
              <div className="flex items-center space-x-4 text-xs text-text-secondary mb-3">
                <div className="flex items-center space-x-1">
                  <Icon name="Calendar" size={12} />
                  <span>{new Date(article.publishDate).toLocaleDateString('es-CR')}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="Clock" size={12} />
                  <span>{article.readTime}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="User" size={12} />
                  <span>{article.author}</span>
                </div>
              </div>

              <h3 className="text-xl font-heading font-semibold text-text-primary mb-3 line-clamp-2 group-hover:text-primary nav-transition">
                {article.title}
              </h3>

              <p className="text-text-secondary text-sm line-clamp-3 mb-4">
                {article.excerpt}
              </p>

              <div className="flex flex-wrap gap-2 mb-4">
                {article.tags.slice(0, 3).map((tag, index) => (
                  <span
                    key={index}
                    className="bg-primary/5 text-primary px-2 py-1 rounded-md text-xs"
                  >
                    #{tag}
                  </span>
                ))}
                {article.tags.length > 3 && (
                  <span className="text-text-secondary text-xs px-2 py-1">
                    +{article.tags.length - 3} más
                  </span>
                )}
              </div>

              <div className="flex items-center justify-between">
                <Link
                  to={`/blog-resources/${article.id}`}
                  className="inline-flex items-center space-x-2 text-primary font-medium hover:text-primary/80 nav-transition"
                >
                  <span>Leer más</span>
                  <Icon name="ArrowRight" size={16} />
                </Link>
                
                <div className="flex items-center space-x-2">
                  <button className="p-2 text-text-secondary hover:text-primary nav-transition">
                    <Icon name="Share2" size={16} />
                  </button>
                  <button className="p-2 text-text-secondary hover:text-error nav-transition">
                    <Icon name="Heart" size={16} />
                  </button>
                </div>
              </div>
            </div>
          </article>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center space-x-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="flex items-center space-x-1 px-3 py-2 rounded-lg border border-border text-text-secondary hover:text-primary hover:border-primary nav-transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Icon name="ChevronLeft" size={16} />
            <span>Anterior</span>
          </button>

          <div className="flex items-center space-x-1">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`w-10 h-10 rounded-lg font-medium nav-transition ${
                  currentPage === page
                    ? 'bg-primary text-white' :'text-text-secondary hover:text-primary hover:bg-primary/10'
                }`}
              >
                {page}
              </button>
            ))}
          </div>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="flex items-center space-x-1 px-3 py-2 rounded-lg border border-border text-text-secondary hover:text-primary hover:border-primary nav-transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span>Siguiente</span>
            <Icon name="ChevronRight" size={16} />
          </button>
        </div>
      )}
    </div>
  );
};

export default ArticleGrid;